@if (!allowed) {
  <mat-card>
    <mat-card-content>Kun superadmin har adgang til adgangsgrupper.</mat-card-content>
  </mat-card>
} @else {
  <mat-card class="cprng-access-groups-card">
    <mat-card-header>
      <mat-card-title>RCMS adgangsgrupper</mat-card-title>
      <mat-card-subtitle>
        Platform-katalog og org-specifik aktivering (adskilt fra brugergrupper i kalenderen)
      </mat-card-subtitle>
    </mat-card-header>
    <mat-card-content>
      @if (loading) {
        <mat-progress-bar mode="indeterminate" />
      }
      @if (errorMessage) {
        <p class="error">{{ errorMessage }}</p>
      }

      <mat-tab-group>
        <mat-tab label="Organisationer">
          <div class="tab-body">
            <p class="hint">
              Vælg en organisation og slå grupper til eller fra. Hvis du ikke har sat en
              org-specifik indstilling, gælder værdien fra fanen <strong>Katalog</strong>
              (&quot;Aktiveret som standard for orgs&quot;).
            </p>
            @if (organizationsError) {
              <p class="error">{{ organizationsError }}</p>
            }
            <mat-form-field appearance="outline" class="org-select">
              <mat-label>Organisation</mat-label>
              <mat-select
                [(ngModel)]="selectedOrgId"
                (selectionChange)="onOrgChange()"
                [disabled]="!organizations.length"
              >
                @for (org of organizations; track org.id) {
                  <mat-option [value]="org.id">{{ org.org_name }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            @if (!organizations.length && !organizationsError) {
              <p class="muted">Ingen organisationer fundet.</p>
            } @else if (selectedOrgId != null && !orgMatrix.length && !loading) {
              <p class="muted">Ingen grupper i kataloget endnu — opret dem under fanen Katalog.</p>
            }

            @for (row of orgMatrix; track row.id) {
              <div class="org-row">
                <div>
                  <strong>{{ row.display_name }}</strong>
                  <span class="muted">({{ row.clean_name }})</span>
                  <div class="meta muted">
                    Standard i katalog: {{ row.enabled_by_default ? 'aktiv' : 'inaktiv' }}
                    @if (row.has_org_override) {
                      · Org har egen indstilling
                    } @else {
                      · Følger standard (skift toggle for at overstyre)
                    }
                  </div>
                </div>
                <mat-slide-toggle
                  [checked]="row.is_enabled_for_org === true"
                  (change)="toggleOrgGroup(row, $event.checked)"
                >
                  Aktiv for denne org
                </mat-slide-toggle>
              </div>
            }
          </div>
        </mat-tab>

        <mat-tab label="Testbrugere">
          <div class="tab-body">
            <p class="hint">
              <strong>users_id 4–8</strong> flyttes altid sammen til samme organisation, og
              <strong>identity_types id 11</strong> (fælles test-Keycloak) sættes til samme org.
              Berørte brugere skal typisk logge ind igen bagefter.
            </p>
            @if (testUserBindingsLoading) {
              <mat-progress-bar mode="indeterminate" />
            }
            @if (testUserBindingsError) {
              <p class="error">{{ testUserBindingsError }}</p>
            }
            <div class="test-users-bulk">
              @if (identityTypesId != null) {
                <p class="identity-types-line">
                  identity_types #{{ identityTypesId }} →
                  {{ orgNameById(identityTypesOrganizationsId) }}
                </p>
              }
              <mat-form-field appearance="outline" class="org-select">
                <mat-label>Organisation for alle testbrugere</mat-label>
                <mat-select
                  [(ngModel)]="pendingTestUsersOrgId"
                  [disabled]="!organizations.length || savingTestUsers"
                >
                  @for (org of organizations; track org.id) {
                    <mat-option [value]="org.id">{{ org.org_name }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <button
                mat-raised-button
                color="primary"
                (click)="applyTestUsersOrganization()"
                [disabled]="
                  savingTestUsers ||
                  pendingTestUsersOrgId == null ||
                  !organizations.length
                "
              >
                Anvend for alle (4–8)
              </button>
            </div>
            @for (row of testUserBindings; track row.users_id) {
              <div class="org-row test-user-row">
                <div>
                  <strong>User {{ row.users_id }}</strong>
                  @if (row.full_name) {
                    <span class="muted">{{ row.full_name }}</span>
                  }
                  <div class="meta muted">
                    Nuværende org:
                    @if (row.organizations_users_found) {
                      {{ orgNameById(row.organizations_id) }}
                    } @else {
                      <span class="error">ingen aktiv organizations_users-række</span>
                    }
                  </div>
                </div>
              </div>
            }
            @if (!testUserBindings.length && !testUserBindingsLoading && !testUserBindingsError) {
              <p class="muted">Ingen testbrugere returneret fra API.</p>
            }
          </div>
        </mat-tab>

        <mat-tab label="Katalog">
          <div class="tab-body">
            <p class="hint">
              Her oprettes grupperne. &quot;Aktiveret som standard for orgs&quot; gælder for alle
              organisationer, medmindre du overstyre på fanen <strong>Organisationer</strong>.
            </p>
            <div class="create-form">
              <mat-form-field appearance="outline">
                <mat-label>clean_name</mat-label>
                <input matInput [(ngModel)]="newGroup.clean_name" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Visningsnavn</mat-label>
                <input matInput [(ngModel)]="newGroup.display_name" />
              </mat-form-field>
              <mat-form-field appearance="outline" class="wide">
                <mat-label>Beskrivelse</mat-label>
                <input matInput [(ngModel)]="newGroup.description" />
              </mat-form-field>
              <mat-slide-toggle [(ngModel)]="newGroup.enabled_by_default">
                Aktiveret som standard for orgs
              </mat-slide-toggle>
              <button mat-raised-button color="primary" (click)="createGroup()">
                <mat-icon>add</mat-icon>
                Opret
              </button>
            </div>

            <table mat-table [dataSource]="catalog" class="groups-table">
              <ng-container matColumnDef="clean_name">
                <th mat-header-cell *matHeaderCellDef>clean_name</th>
                <td mat-cell *matCellDef="let row">{{ row.clean_name }}</td>
              </ng-container>
              <ng-container matColumnDef="display_name">
                <th mat-header-cell *matHeaderCellDef>Navn</th>
                <td mat-cell *matCellDef="let row">{{ row.display_name }}</td>
              </ng-container>
              <ng-container matColumnDef="enabled_by_default">
                <th mat-header-cell *matHeaderCellDef>Standard</th>
                <td mat-cell *matCellDef="let row">
                  {{ row.enabled_by_default ? 'Ja' : 'Nej' }}
                </td>
              </ng-container>
              <ng-container matColumnDef="sort_order">
                <th mat-header-cell *matHeaderCellDef>Sortering</th>
                <td mat-cell *matCellDef="let row">{{ row.sort_order }}</td>
              </ng-container>
              <ng-container matColumnDef="actions">
                <th mat-header-cell *matHeaderCellDef></th>
                <td mat-cell *matCellDef="let row">
                  <button
                    mat-icon-button
                    color="warn"
                    (click)="deleteGroup(row)"
                    matTooltip="Slet (soft)"
                  >
                    <mat-icon>delete</mat-icon>
                  </button>
                </td>
              </ng-container>
              <tr mat-header-row *matHeaderRowDef="catalogColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: catalogColumns"></tr>
            </table>
          </div>
        </mat-tab>
      </mat-tab-group>
    </mat-card-content>
  </mat-card>
}
